import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, type CloneInfo, type CourseDetail, type Version } from "../api";
import { ChatPanel } from "../components/ChatPanel";
import { DocumentView, type DocumentHandle } from "../components/DocumentView";
import { FaqPanel } from "../components/FaqPanel";
import { TocSidebar } from "../components/TocSidebar";
import { TopBar } from "../components/TopBar";
import type { TocItem } from "../html";

type RightTab = "chat" | "faq";
type MobilePane = "doc" | "toc" | "assistant";

export function CoursePage() {
  const { slug = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const version: Version = params.get("v") === "mine" ? "mine" : "original";

  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tab, setTab] = useState<RightTab>("chat");
  const [mobilePane, setMobilePane] = useState<MobilePane>("doc");
  const [faqKey, setFaqKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [contentKey, setContentKey] = useState(0); // bump to reload the document
  const docRef = useRef<DocumentHandle>(null);

  const setVersion = useCallback(
    (v: Version) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === "mine") next.set("v", "mine");
          else next.delete("v");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    api.course(slug).then(setDetail).catch((e) => setError(e.message));
  }, [slug]);

  const hasClone = detail?.clone.exists ?? false;
  const effectiveVersion: Version = version === "mine" && detail && !hasClone ? "original" : version;

  useEffect(() => {
    if (!detail) return;
    if (version === "mine" && !hasClone) {
      setVersion("original");
      return;
    }
    let cancelled = false;
    api
      .content(slug, effectiveVersion)
      .then((c) => !cancelled && setHtml(c.html))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [slug, detail, effectiveVersion, version, hasClone, setVersion, contentKey]);

  useEffect(() => {
    if (detail) document.title = `${detail.title} · AI Courses`;
  }, [detail]);

  const onCloneChanged = useCallback(
    (clone: CloneInfo, changed: boolean) => {
      setDetail((d) => (d ? { ...d, clone } : d));
      if (changed) {
        setVersion("mine");
        setContentKey((k) => k + 1);
        showToast("Your copy was updated. Showing My copy.");
      }
    },
    [setVersion, showToast],
  );

  async function undo() {
    try {
      const clone = await api.undoClone(slug);
      setDetail((d) => (d ? { ...d, clone } : d));
      setContentKey((k) => k + 1);
      showToast("Last edit undone.");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not undo");
    }
  }

  async function reset() {
    setConfirmReset(false);
    try {
      const clone = await api.resetClone(slug);
      setDetail((d) => (d ? { ...d, clone } : d));
      setVersion("original");
      showToast("Your copy was deleted. The next edit starts fresh from the original.");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not reset");
    }
  }

  const navigate = useCallback((id: string) => {
    // On phones the document pane is hidden while the contents are open; show
    // it first and scroll once it has been laid out.
    setMobilePane("doc");
    requestAnimationFrame(() => docRef.current?.scrollTo(id));
  }, []);

  if (error) {
    return (
      <div className="app-shell">
        <TopBar />
        <main className="catalog">
          <p className="form-error">{error}</p>
        </main>
      </div>
    );
  }

  const clone = detail?.clone;

  return (
    <div className={`app-shell course-shell mobile-${mobilePane}`}>
      <TopBar>
        <div className="course-heading">
          <span className="course-title" title={detail?.title}>
            {detail?.title ?? "Loading…"}
          </span>
          <div className="version-switch" role="group" aria-label="Document version">
            <button
              className={effectiveVersion === "original" ? "seg active" : "seg"}
              aria-pressed={effectiveVersion === "original"}
              onClick={() => setVersion("original")}
            >
              Original
            </button>
            <button
              className={effectiveVersion === "mine" ? "seg active" : "seg"}
              aria-pressed={effectiveVersion === "mine"}
              disabled={!hasClone}
              title={hasClone ? "Your edited copy" : "Turn on “Edit my copy” in the chat to create your own copy"}
              onClick={() => setVersion("mine")}
            >
              My copy{clone?.exists && clone.edit_count > 0 ? ` · ${clone.edit_count} edit${clone.edit_count === 1 ? "" : "s"}` : ""}
            </button>
          </div>
          {clone?.exists && (
            <div className="clone-actions">
              <button className="btn btn-sm btn-ghost" onClick={() => void undo()} disabled={!clone.can_undo}>
                Undo last edit
              </button>
              {confirmReset ? (
                <>
                  <button className="btn btn-sm btn-danger" onClick={() => void reset()}>
                    Delete my copy
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setConfirmReset(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmReset(true)}>
                  Reset to original
                </button>
              )}
            </div>
          )}
        </div>
      </TopBar>

      <nav className="mobile-tabs" aria-label="Panels">
        <button className={mobilePane === "toc" ? "active" : ""} onClick={() => setMobilePane("toc")}>
          Contents
        </button>
        <button className={mobilePane === "doc" ? "active" : ""} onClick={() => setMobilePane("doc")}>
          Course
        </button>
        <button className={mobilePane === "assistant" ? "active" : ""} onClick={() => setMobilePane("assistant")}>
          Assistant
        </button>
      </nav>

      <div className="course-layout">
        <aside className="col-toc">
          <TocSidebar toc={toc} activeId={activeId} onNavigate={navigate} />
        </aside>

        <main className="col-doc">
          {effectiveVersion === "mine" && clone?.original_newer && (
            <div className="banner">
              The original course has been updated since you made your copy.{" "}
              <button className="link-btn" onClick={() => setConfirmReset(true)}>
                Reset to get the latest
              </button>
            </div>
          )}
          {effectiveVersion === "mine" && <div className="viewing-mine">Viewing your copy</div>}
          {html === null ? (
            <div className="doc-pane">
              <p className="muted doc-loading">Loading course…</p>
            </div>
          ) : (
            <DocumentView ref={docRef} html={html} onToc={setToc} onActiveChange={setActiveId} />
          )}
        </main>

        <aside className="col-assistant">
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={tab === "chat"} className={tab === "chat" ? "tab active" : "tab"} onClick={() => setTab("chat")}>
              Ask Claude
            </button>
            <button role="tab" aria-selected={tab === "faq"} className={tab === "faq" ? "tab active" : "tab"} onClick={() => setTab("faq")}>
              My FAQ
            </button>
          </div>
          <div className="tab-panel" hidden={tab !== "chat"}>
            {detail && (
              <ChatPanel
                slug={slug}
                viewing={effectiveVersion}
                cloneExists={hasClone}
                onCloneChanged={onCloneChanged}
                onFaqChanged={() => setFaqKey((k) => k + 1)}
              />
            )}
          </div>
          <div className="tab-panel" hidden={tab !== "faq"}>
            <FaqPanel slug={slug} refreshKey={faqKey} />
          </div>
        </aside>
      </div>

      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
